// New Lumotis service pages — data-driven via ServicePage.jsx.
// Each entry powers a full landing page at /<slug>.

export const SERVICE_LIST = [
  /* ──────────────────── CRO ──────────────────── */
  {
    slug: "cro",
    name: "Conversion Rate Optimization",
    shortName: "CRO",
    emoji: "📈",
    cover:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=85",
    metaTitle:
      "Conversion Rate Optimization (CRO) Agency — A/B Testing & Funnel Optimisation | Lumotis",
    metaDescription:
      "Lumotis is a specialist CRO agency. We turn your existing website traffic into 2–5× more leads and sales with A/B testing, heatmap analysis, funnel optimisation and landing-page redesign. Free CRO audit.",
    heroEyebrow: "Conversion Rate Optimization",
    heroHeadline: "Stop Buying More Traffic — Convert the Visitors You Already Have",
    heroAccent: "2–5× more leads and sales from the same traffic.",
    heroTagline:
      "We run rigorous A/B tests, heatmap analysis and funnel optimisation against your live site — finding and fixing the points where visitors abandon. Most clients see double-digit conversion lifts within 60 days.",
    heroStats: [
      { value: "+87%", label: "Avg conversion rate lift" },
      { value: "120+", label: "A/B tests run for clients" },
      { value: "60 days", label: "Avg time to measurable win" },
    ],
    insightStats: [
      {
        stat: "68%",
        label: "of users abandon at checkout / form submission",
        desc: "The biggest revenue leak is rarely on the homepage — it's on the page where users actually convert. Most businesses never test this page.",
      },
      {
        stat: "9×",
        label: "ROI of CRO vs paid acquisition for established brands",
        desc: "Every dollar invested in CRO compounds across all your traffic — paid, organic, social, referral. It's the highest-leverage marketing investment most brands have available.",
      },
      {
        stat: "37%",
        label: "of sites have never run a single A/B test",
        desc: "Most businesses guess at what works. Systematic testing eliminates guesswork and builds a permanent conversion advantage over competitors.",
      },
    ],
    pillars: [
      {
        title: "Conversion Audit & Funnel Mapping",
        body: "We map every step of your user journey — from first click to purchase / lead capture — and identify the top 5 revenue leaks. No assumptions, no opinions — just data on where visitors are dropping off.",
      },
      {
        title: "Heatmaps & Session Recordings",
        body: "Hotjar / Microsoft Clarity setup with custom event tracking. We watch real users interact with your site — every click, scroll, rage-tap and abandonment — to understand the 'why' behind the drop-offs.",
      },
      {
        title: "A/B & Multivariate Testing",
        body: "VWO / Optimizely / Google Optimize / Convert.com test architecture. Statistically valid experiments on headlines, CTAs, form fields, layouts and pricing. We don't roll out winners until 95% confidence is reached.",
      },
      {
        title: "Landing Page Redesign",
        body: "Conversion-focused page rebuilds for paid traffic, organic search and email campaigns. Mobile-first design with proven CRO patterns — social proof placement, friction reduction, urgency triggers.",
      },
      {
        title: "Checkout & Form Optimisation",
        body: "The highest-impact area of any site. We strip unnecessary form fields, add trust signals, fix mobile checkout, and rebuild abandonment flows. Most clients see 20-40% lift in completion rates within 30 days.",
      },
      {
        title: "Personalisation & Dynamic Content",
        body: "Show different messaging to first-time visitors vs returning users, paid vs organic traffic, mobile vs desktop. AI-driven personalisation engines unlock another 15-30% lift on top of base CRO work.",
      },
    ],
    caseStudy: {
      metric: "+147%",
      metricLabel: "conversion rate in 90 days",
      badge: "Ecommerce · USA · Heatmaps + A/B Testing + Checkout Redesign",
      title: "DTC Skincare Brand — from 1.8% to 4.4% conversion rate.",
      body: "A US skincare ecommerce brand was driving 50,000 monthly visitors but converting at 1.8%. Lumotis ran a 90-day CRO sprint: heatmap analysis identified mobile cart abandonment as the #1 issue, 14 A/B tests on PDP layouts, and a complete checkout redesign. End result: conversion rate up to 4.4%, monthly revenue up $180K, with zero increase in ad spend.",
    },
    results: [
      { metric: "+147%", label: "conversion rate lift", market: "USA · DTC Skincare" },
      { metric: "+$180K", label: "monthly revenue gain", market: "Same traffic" },
      { metric: "14", label: "winning A/B tests", market: "90-day sprint" },
      { metric: "-38%", label: "checkout abandonment", market: "Mobile users" },
    ],
    tactics: [
      "Heatmap analysis (Hotjar/Clarity)",
      "Session recording review",
      "Form field analytics",
      "Funnel drop-off mapping",
      "A/B test architecture",
      "Multivariate testing",
      "Landing page redesigns",
      "Hero section testing",
      "CTA button optimisation",
      "Social proof placement",
      "Trust signal A/B tests",
      "Form field reduction",
      "Checkout flow redesign",
      "Cart abandonment recovery",
      "Mobile UX optimisation",
      "Page speed CRO impact",
      "Personalisation rules",
      "Dynamic content blocks",
      "Exit intent overlays",
      "Pricing presentation tests",
    ],
    faq: [
      {
        q: "How quickly will CRO show results?",
        a: "Initial wins typically appear within 30-60 days. We always run statistically valid tests — meaning we need enough traffic and time to reach 95% confidence before declaring a winner. Pages with high traffic see results faster; lower-traffic pages may need 6-8 weeks to validate each test.",
      },
      {
        q: "How much traffic do we need for CRO to work?",
        a: "We recommend at least 5,000 monthly visitors to a page being tested for meaningful statistical significance. Below that, we focus on qualitative research (heatmaps, recordings, user testing) and best-practice redesigns rather than A/B tests.",
      },
      {
        q: "Do you handle all the implementation, or just recommendations?",
        a: "Full implementation. Our team builds the tests, codes the variations, deploys them, monitors results, and writes the post-test analysis. You don't need a developer on your end — though we work seamlessly with your existing dev team if you have one.",
      },
      {
        q: "Can we add CRO on top of our existing marketing?",
        a: "Yes — and we recommend it. CRO compounds the ROI of everything else you're spending on traffic. Most clients add CRO as a complement to existing SEO or paid ads engagements, and see overall marketing ROI improve 30-60% within the first quarter.",
      },
    ],
    relatedServices: ["content-marketing", "web-design", "marketing-automation"],
  },

  /* ──────────────── Content Marketing ──────────────── */
  {
    slug: "content-marketing",
    name: "Content Marketing & Copywriting",
    shortName: "Content",
    emoji: "✍️",
    cover:
      "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&q=85",
    metaTitle:
      "Content Marketing Agency — SEO Content, Blogs & Copywriting | Lumotis",
    metaDescription:
      "Lumotis is a full-stack content marketing agency. Strategy, SEO blog content, pillar pages, lead magnets and conversion copy that ranks on Google and converts on every channel. Free content audit.",
    heroEyebrow: "Content Marketing & Copywriting",
    heroHeadline: "Content That Ranks on Google and Converts on Every Channel",
    heroAccent: "From strategy to publication — done for you.",
    heroTagline:
      "We build content engines that drive organic traffic, generate leads and convert customers. SEO-first blog content, pillar pages, lead magnets, email sequences and conversion copy — all produced in-house by senior strategists and editors.",
    heroStats: [
      { value: "300%", label: "Avg organic traffic growth" },
      { value: "2,500+", label: "Articles published for clients" },
      { value: "$8M+", label: "Pipeline generated from content" },
    ],
    insightStats: [
      {
        stat: "70%",
        label: "of B2B buyers consume 3+ pieces of content before making a purchase decision",
        desc: "Content marketing isn't optional anymore — it's how trust gets built. Without consistent, high-quality content, prospects choose your competitors who have it.",
      },
      {
        stat: "13×",
        label: "more leads from companies publishing 11+ blog posts per month",
        desc: "Consistency compounds. Companies that publish frequently and consistently dominate organic search and build email lists faster than competitors who don't.",
      },
      {
        stat: "62%",
        label: "lower cost per lead from content marketing vs paid ads",
        desc: "Once content is published, it generates leads indefinitely — for free. Every article you publish becomes a permanent revenue-generating asset on your balance sheet.",
      },
    ],
    pillars: [
      {
        title: "Content Strategy & Keyword Mapping",
        body: "We map your industry's keyword universe — 200+ relevant topics — cluster them by search intent, and prioritise by traffic potential and conversion likelihood. Every piece of content has a clear purpose and target keyword.",
      },
      {
        title: "Pillar Pages & Topic Clusters",
        body: "Long-form authoritative resources (3,000-8,000 words) that rank for high-value head terms, plus supporting cluster articles that link to them. This is the Google-approved structure that wins competitive niches.",
      },
      {
        title: "SEO-First Blog Content",
        body: "Weekly or bi-weekly long-form articles (1,500-3,000 words) optimised for ranking and reading. Senior editors brief and review every piece. We never publish thin content.",
      },
      {
        title: "Lead Magnets & Gated Content",
        body: "Ebooks, whitepapers, calculators, templates and toolkits that convert organic traffic into email subscribers. Lumotis-produced design + copy → email-list growth.",
      },
      {
        title: "Email Newsletter & Sequences",
        body: "Welcome series, nurture sequences, sales follow-ups and weekly newsletters. We write, design and schedule everything inside your existing ESP (Klaviyo, ActiveCampaign, Mailchimp, HubSpot).",
      },
      {
        title: "Conversion Copywriting",
        body: "Sales pages, landing pages, ad copy and product descriptions written by senior conversion copywriters. Frameworks proven across 200+ funnels — PAS, AIDA, before/after/bridge, and our own tested templates.",
      },
    ],
    caseStudy: {
      metric: "+412%",
      metricLabel: "organic traffic in 12 months",
      badge: "SaaS · UK · Pillar Pages + Weekly Blog + Lead Magnets",
      title: "B2B SaaS Brand — from 8K to 41K monthly organic visitors.",
      body: "A UK B2B SaaS company had 8,000 monthly visitors and a stalled content programme. Lumotis built a 12-month content strategy with 5 pillar pages, 96 supporting articles and 4 high-value lead magnets. Twelve months later: 41,000 monthly organic visitors, 1,800 new email subscribers per month, and £2.4M of pipeline generated from content alone.",
    },
    results: [
      { metric: "+412%", label: "organic traffic", market: "UK · B2B SaaS" },
      { metric: "1,800", label: "monthly new email subs", market: "Lead magnets" },
      { metric: "£2.4M", label: "pipeline from content", market: "12-month run" },
      { metric: "127", label: "ranking keywords on page 1", market: "Up from 11" },
    ],
    tactics: [
      "Keyword research & clustering",
      "Topic cluster strategy",
      "Pillar page content briefs",
      "Long-form blog articles (1,500–3,000 words)",
      "SEO content optimisation",
      "Featured snippet targeting",
      "Schema markup for articles",
      "Internal linking strategy",
      "Lead magnet creation",
      "Ebook design & writing",
      "Whitepaper production",
      "Calculator / quiz creation",
      "Email newsletter copywriting",
      "Welcome email series",
      "Nurture sequences",
      "Sales page copywriting",
      "Landing page copy",
      "Ad copy (Google + Meta)",
      "Product description writing",
      "Content distribution & syndication",
    ],
    faq: [
      {
        q: "How long until content marketing shows results?",
        a: "First measurable organic traffic growth typically appears within 3-4 months. Significant traffic and lead generation usually compound from month 6 onwards. Content marketing is a long-term investment that pays compounding returns — most of our clients see 12-month ROI of 5-15× their content spend.",
      },
      {
        q: "Who writes the content — your team or AI?",
        a: "Senior human writers write everything. We use AI tools for research and outlining, but every piece is written, edited and reviewed by experienced human writers and editors. AI-generated content gets devalued by Google and reads as bland — we don't ship it.",
      },
      {
        q: "Do you work with our existing brand voice?",
        a: "Yes. Onboarding includes a brand voice workshop where we capture your tone, terminology, and style. Every writer assigned to your account follows your custom style guide. Most clients can't distinguish our content from in-house work after the first 2-3 articles.",
      },
      {
        q: "What's the minimum content volume to see results?",
        a: "We recommend a minimum of 4-8 high-quality long-form articles per month for B2C, and 4-6 per month for B2B niches. Below that, growth is too slow to compound. Above that, growth accelerates significantly — but quality must be maintained.",
      },
    ],
    relatedServices: ["cro", "marketing-automation", "link-building"],
  },

  /* ──────────────── Web Design ──────────────── */
  {
    slug: "web-design",
    name: "Website Design & Development",
    shortName: "Web Design",
    emoji: "🎨",
    cover:
      "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=1200&q=85",
    metaTitle:
      "Website Design & Development Agency — Conversion-Focused Websites | Lumotis",
    metaDescription:
      "Lumotis designs and builds high-converting websites that grow revenue. Custom design, Webflow, Shopify, WordPress and headless development. SEO-optimised, fast, conversion-focused. Free website audit.",
    heroEyebrow: "Website Design & Development",
    heroHeadline: "Websites That Look Stunning — And Actually Convert",
    heroAccent: "Built by designers who understand revenue.",
    heroTagline:
      "We design and build websites that load fast, rank on Google, look exceptional and turn visitors into customers. Webflow, Shopify, WordPress, headless — whichever platform best fits your business, built with conversion best practices baked in from day one.",
    heroStats: [
      { value: "150+", label: "Websites launched" },
      { value: "+62%", label: "Avg conversion lift post-launch" },
      { value: "90/100", label: "Avg Lighthouse score delivered" },
    ],
    insightStats: [
      {
        stat: "75%",
        label: "of users judge a business's credibility by its website design",
        desc: "Your website is the most important sales asset you own. A dated or slow website costs you more in lost trust than the entire rebuild project — every single day it stays live.",
      },
      {
        stat: "53%",
        label: "of mobile users abandon sites that take longer than 3 seconds to load",
        desc: "Speed isn't just a UX issue — it's a direct revenue issue. Every additional second of load time reduces conversion by 7%. Most websites we audit load in 5-8 seconds.",
      },
      {
        stat: "2.6×",
        label: "conversion rate of mobile-optimised vs non-optimised sites",
        desc: "Over 60% of web traffic is now mobile. Sites built mobile-first convert dramatically better — and Google now indexes mobile-first too, so it's a ranking factor.",
      },
    ],
    pillars: [
      {
        title: "Strategy & Discovery",
        body: "We start with your business goals, target audience, conversion path and competitive positioning. The output is a sitemap, content strategy and design brief — agreed before any pixel is pushed.",
      },
      {
        title: "Conversion-Focused Design",
        body: "Every page is designed around a single primary action. Hierarchy of information, social proof placement, friction reduction, mobile-first layout — all dictated by what we know converts from running 120+ A/B tests.",
      },
      {
        title: "Custom Development (Multi-Platform)",
        body: "Webflow for marketing sites, Shopify for ecommerce, WordPress for content-heavy sites, custom headless (Next.js + Sanity/Contentful) for performance-critical projects. We pick the right tool, not push a default.",
      },
      {
        title: "SEO Architecture",
        body: "Built-in technical SEO: schema markup, semantic HTML, optimised metadata, fast Core Web Vitals, image optimisation, sitemap and robots.txt. Your new site ranks faster than your old one — guaranteed.",
      },
      {
        title: "Speed & Core Web Vitals",
        body: "Every site we ship hits 90+ Lighthouse scores. WebP/AVIF images, code splitting, lazy loading, CDN, minimal third-party scripts. Fast sites convert better, rank better and reduce ad CPCs.",
      },
      {
        title: "Launch + Ongoing Optimisation",
        body: "We don't disappear after launch. Every Lumotis-built website gets 90 days of free optimisation: CRO sprints, content updates, performance tuning, analytics review. Optional ongoing retainer for continuous improvement.",
      },
    ],
    caseStudy: {
      metric: "+218%",
      metricLabel: "qualified leads after relaunch",
      badge: "Professional Services · Australia · Webflow + SEO Migration",
      title: "AU Legal Firm — from outdated WordPress to a lead-generating Webflow site.",
      body: "A Melbourne legal practice had a 9-year-old WordPress site converting at under 1%. Lumotis redesigned and rebuilt the entire site on Webflow with CRO-driven layout, lead-capture funnels, and full SEO migration. Three months post-launch: 218% more qualified leads, page speed up from 32 to 96 Lighthouse, organic rankings up across 47 high-intent keywords.",
    },
    results: [
      { metric: "+218%", label: "qualified leads", market: "AU · Legal" },
      { metric: "32 → 96", label: "Lighthouse performance", market: "Mobile" },
      { metric: "+47", label: "first-page keyword rankings", market: "Post-migration" },
      { metric: "-44%", label: "bounce rate", market: "All traffic" },
    ],
    tactics: [
      "Discovery workshop",
      "Information architecture",
      "Sitemap design",
      "Wireframing",
      "Visual design (Figma)",
      "Custom design system",
      "Webflow development",
      "Shopify theme development",
      "WordPress development",
      "Headless / Next.js development",
      "CMS integration",
      "E-commerce setup",
      "Form & lead capture build",
      "CRM integration",
      "Email tool integration",
      "Schema markup",
      "Technical SEO build",
      "Core Web Vitals optimisation",
      "Image optimisation (WebP/AVIF)",
      "Analytics & tracking setup",
    ],
    faq: [
      {
        q: "What platforms do you build on?",
        a: "Webflow (most marketing sites), Shopify (ecommerce), WordPress (content-heavy sites, especially when migrations are required), and custom Next.js + headless CMS for performance-critical projects. We recommend the right platform for your specific needs — we don't push a default.",
      },
      {
        q: "How long does a typical project take?",
        a: "Marketing sites (5-15 pages): 4-8 weeks. Ecommerce stores: 6-12 weeks. Custom builds: 10-20 weeks. We provide a detailed Gantt chart during discovery with clear milestones and approvals at each stage.",
      },
      {
        q: "What does it cost?",
        a: "Most projects range from $8,000 (small marketing site) to $80,000 (custom enterprise builds). Average mid-tier ecommerce or B2B site is $15,000-$30,000. Pricing depends on page count, complexity, integrations and custom design needs. We provide fixed-price quotes after discovery.",
      },
      {
        q: "Do you handle hosting, maintenance and content updates?",
        a: "Yes — we offer optional ongoing care plans starting at $250/month covering hosting, security, backups, content updates, performance monitoring and minor design tweaks. Most clients add this to avoid the headache of managing the site themselves.",
      },
    ],
    relatedServices: ["cro", "content-marketing", "marketing-automation"],
  },

  /* ──────────────── Marketing Automation ──────────────── */
  {
    slug: "marketing-automation",
    name: "Marketing Automation Setup",
    shortName: "Automation",
    emoji: "⚡",
    cover:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=85",
    metaTitle:
      "Marketing Automation Agency — HubSpot, Klaviyo, ActiveCampaign Setup | Lumotis",
    metaDescription:
      "Lumotis is a certified marketing automation agency. We architect, implement and optimise HubSpot, Klaviyo, ActiveCampaign, Go High Level and other platforms. Free automation audit.",
    heroEyebrow: "Marketing Automation Setup",
    heroHeadline: "Automate the Marketing Workflows That Are Eating Your Time",
    heroAccent: "Set it up once. Generate revenue forever.",
    heroTagline:
      "We architect, build and optimise marketing automation workflows that nurture leads, recover lost sales, and onboard customers — automatically, 24/7. Certified across HubSpot, Klaviyo, ActiveCampaign, Go High Level, Salesforce and Pardot.",
    heroStats: [
      { value: "180+", label: "Automation systems built" },
      { value: "$12M+", label: "Revenue automated" },
      { value: "8 platforms", label: "Certified expertise" },
    ],
    insightStats: [
      {
        stat: "451%",
        label: "more qualified leads from companies using marketing automation",
        desc: "Automation isn't a 'nice to have' — it's a competitive moat. Companies running sophisticated automation generate 4.5× more qualified leads than competitors who don't.",
      },
      {
        stat: "30%",
        label: "of email revenue comes from automated flows (not broadcasts)",
        desc: "A well-built welcome series, abandoned cart sequence and post-purchase flow consistently generates 25-40% of total email revenue — with zero ongoing manual work after setup.",
      },
      {
        stat: "77%",
        label: "of marketers report higher conversion rates after automation implementation",
        desc: "Lead nurturing, behavioural triggers and personalisation lift conversion rates dramatically. The data is overwhelming — automation works, when it's built correctly.",
      },
    ],
    pillars: [
      {
        title: "Platform Audit & Stack Architecture",
        body: "We audit your current tech stack, identify gaps, and design the right architecture for your business. CRM, email, SMS, ads, analytics — all integrated into a single source of truth.",
      },
      {
        title: "CRM Implementation & Migration",
        body: "Full HubSpot / Salesforce / Pipedrive / Go High Level builds. Custom properties, deal stages, lifecycle stages, lead scoring, sales pipelines, reporting dashboards. Migrations from your old system handled cleanly.",
      },
      {
        title: "Email Flow Architecture",
        body: "Welcome sequences, lead nurture flows, abandoned cart recovery, post-purchase sequences, win-back campaigns, VIP loyalty flows. Built in Klaviyo, ActiveCampaign, HubSpot, or your platform of choice.",
      },
      {
        title: "Behavioural Triggers & Segmentation",
        body: "Action-based triggers (visited page X, downloaded Y, watched Z) feed into hyper-targeted sequences. Segmentation rules update in real-time based on engagement, purchase history, and predicted lifetime value.",
      },
      {
        title: "Lead Scoring & Routing",
        body: "MQL / SQL scoring rules based on behaviour, firmographics and engagement. Hot leads route to sales reps instantly. Cold leads stay in nurture. Sales gets only the leads worth calling.",
      },
      {
        title: "Reporting & Continuous Optimisation",
        body: "Custom dashboards showing flow performance, revenue attribution, lead source ROI and pipeline velocity. Monthly optimisation reviews — we evolve flows as your business grows.",
      },
    ],
    caseStudy: {
      metric: "$1.2M",
      metricLabel: "annual revenue automated",
      badge: "Ecommerce · USA · Klaviyo Flow Architecture + HubSpot CRM",
      title: "US DTC Brand — from manual emails to fully automated revenue engine.",
      body: "A US DTC ecommerce brand was sending campaigns manually and had no abandoned cart flow. Lumotis built 12 Klaviyo flows (welcome, abandoned cart, browse abandon, post-purchase, win-back, VIP, birthday, replenishment) and integrated HubSpot for B2B wholesale leads. Within 6 months: $1.2M of annual revenue flowing through automations, 38% of total email revenue from flows, and zero manual email work for the marketing team.",
    },
    results: [
      { metric: "$1.2M", label: "annual revenue automated", market: "USA · DTC" },
      { metric: "12 flows", label: "Klaviyo automation library", market: "Built + tested" },
      { metric: "38%", label: "email revenue from flows", market: "Industry avg: 18%" },
      { metric: "5.7×", label: "ROI on automation build", market: "12-month payback" },
    ],
    tactics: [
      "Tech stack audit",
      "Automation architecture design",
      "HubSpot CRM build",
      "Salesforce implementation",
      "Pipedrive setup",
      "Go High Level builds",
      "Klaviyo flow architecture",
      "ActiveCampaign workflows",
      "Mailchimp automations",
      "Welcome email series",
      "Abandoned cart sequences",
      "Browse abandonment flows",
      "Post-purchase sequences",
      "Win-back campaigns",
      "Lead scoring rules",
      "Lifecycle stage automation",
      "SMS automation (Attentive/Postscript)",
      "Multi-channel orchestration",
      "Custom dashboards",
      "Zapier / Make integrations",
    ],
    faq: [
      {
        q: "Which platforms are you certified on?",
        a: "HubSpot (Gold partner), Klaviyo (Master partner), ActiveCampaign (certified), Go High Level (white-label partner), Salesforce (admin certified), Pipedrive, Mailchimp, and Attentive. We work in others too, but these are where our deepest expertise lives.",
      },
      {
        q: "Do you handle migrations from one platform to another?",
        a: "Yes — migrations are one of our most-requested services. Common moves: Mailchimp → Klaviyo (ecommerce), Pipedrive → HubSpot (scaling B2B), legacy → Go High Level (agencies). We handle data migration, flow rebuilding, and team training end-to-end.",
      },
      {
        q: "How long does a typical automation build take?",
        a: "Foundational setup (CRM + 5-6 core flows): 4-6 weeks. Full enterprise build (15+ flows, multi-channel, lead scoring, reporting): 10-14 weeks. We deliver in phases so you start seeing ROI before the full project is complete.",
      },
      {
        q: "Do you train our team to run automation after handover?",
        a: "Yes — knowledge transfer is built into every engagement. We deliver SOPs, training videos and live training sessions. Many clients then add an ongoing retainer for ongoing flow optimisation and new flow builds, but most can manage day-to-day operations independently.",
      },
    ],
    relatedServices: ["cro", "content-marketing", "web-design"],
  },

  /* ──────────────── Link Building & Digital PR ──────────────── */
  {
    slug: "link-building",
    name: "Link Building & Digital PR",
    shortName: "Link Building",
    emoji: "🔗",
    cover:
      "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200&q=85",
    metaTitle:
      "Link Building & Digital PR Agency — High-Authority Backlinks | Lumotis",
    metaDescription:
      "Lumotis is a white-hat link building and digital PR agency. We earn high-authority backlinks from DR 50+ sites via outreach, digital PR, HARO, broken-link building and content partnerships. Free link audit.",
    heroEyebrow: "Link Building & Digital PR",
    heroHeadline: "High-Authority Backlinks That Move the Needle on Search Rankings",
    heroAccent: "White-hat outreach. DR 50+ sites. Real results.",
    heroTagline:
      "We earn high-quality backlinks from authoritative websites in your niche through manual outreach, digital PR campaigns, HARO responses, broken-link building and content partnerships. Every link is white-hat, contextual and built to last.",
    heroStats: [
      { value: "12,000+", label: "Quality links built for clients" },
      { value: "DR 50+", label: "Avg domain rating of placements" },
      { value: "+185%", label: "Avg organic traffic 12 months later" },
    ],
    insightStats: [
      {
        stat: "#1",
        label: "ranking factor — number of referring domains pointing to your site",
        desc: "Google's algorithm still relies heavily on backlinks as a trust signal. Sites with more high-quality referring domains rank higher than competitors — even when content quality is comparable.",
      },
      {
        stat: "91%",
        label: "of web pages get zero organic traffic from Google",
        desc: "The single biggest reason: not enough quality backlinks. Content alone isn't enough anymore — links are what tell Google your content deserves to rank.",
      },
      {
        stat: "12-month",
        label: "average time for link building investments to deliver full ROI",
        desc: "Link building is a long game. Most measurable ranking improvements appear in 4-6 months, but full compounding ROI takes 12-18 months — and the rankings, once earned, are extremely sticky.",
      },
    ],
    pillars: [
      {
        title: "Backlink Audit & Toxic Link Disavow",
        body: "We audit your existing backlink profile, identify toxic and spammy links damaging your rankings, and submit clean disavow files. Most sites have 5-15% toxic links that should be removed immediately.",
      },
      {
        title: "Manual Outreach Link Building",
        body: "Personalised pitches to relevant high-authority websites in your industry. We earn editorial mentions, resource page additions and guest posts — all from genuinely relevant sites with engaged audiences. Zero networks, zero PBNs.",
      },
      {
        title: "Digital PR Campaigns",
        body: "Data-driven story creation and outreach to journalists at top-tier publications. Forbes, TechCrunch, Inc, Mashable, BBC, Telegraph mentions — backlink and brand awareness combined.",
      },
      {
        title: "HARO / Source of Sources Responses",
        body: "Our team responds to relevant HARO, Qwoted and ProfNet queries on your behalf with expert quotes from your business. Result: ongoing high-DA mentions in business publications worldwide.",
      },
      {
        title: "Broken Link Building",
        body: "Identify broken links on high-authority sites in your niche, then pitch your content as the perfect replacement. One of the highest-converting link tactics — average 15-20% response rate.",
      },
      {
        title: "Content Partnerships & Co-Marketing",
        body: "Joint webinars, co-authored research, partnership badges and brand collaborations with non-competing companies in adjacent verticals. Links come naturally from real business relationships.",
      },
    ],
    caseStudy: {
      metric: "287",
      metricLabel: "DR 50+ backlinks earned",
      badge: "SaaS · UK · 12-month link building + digital PR campaign",
      title: "B2B SaaS — from DR 32 to DR 64 in 12 months.",
      body: "A UK B2B SaaS company had strong content but couldn't crack page 1 for competitive head terms. Lumotis ran a 12-month integrated link building + digital PR campaign: 287 placements on DR 50+ sites including Forbes, TechCrunch, Search Engine Journal and Inc. Result: domain rating up from 32 to 64, organic traffic up 312%, and 18 head-term keywords now ranking in top 3.",
    },
    results: [
      { metric: "287", label: "DR 50+ backlinks earned", market: "UK · B2B SaaS" },
      { metric: "DR 32 → 64", label: "domain rating growth", market: "12-month run" },
      { metric: "+312%", label: "organic traffic growth", market: "Same content" },
      { metric: "18", label: "top-3 head-term rankings", market: "Up from 2" },
    ],
    tactics: [
      "Backlink audit",
      "Toxic link disavow",
      "Competitor link gap analysis",
      "Manual outreach campaigns",
      "Guest post pitching",
      "Resource page link building",
      "Broken link building",
      "Skyscraper technique",
      "Digital PR story creation",
      "Press release distribution",
      "Journalist outreach",
      "HARO response writing",
      "Qwoted / Source of Sources",
      "Statistic & data linkbait",
      "Original research reports",
      "Podcast guest placement",
      "Content partnership outreach",
      "Co-marketing campaigns",
      "Brand mention reclamation",
      "Niche directory submissions",
    ],
    faq: [
      {
        q: "Are your links white-hat and Google-safe?",
        a: "100% white-hat. Every link is earned through editorial relationships, expert contributions or original content placement — never bought, never from PBNs, never from link farms. Lumotis has been running link building since 2018 and we've never had a client penalised by Google.",
      },
      {
        q: "How many links per month should we expect?",
        a: "Quality matters infinitely more than volume. Most engagements deliver 8-25 links per month depending on niche difficulty and budget tier. A typical link will be on a DR 40-70+ site with real organic traffic. We never sacrifice quality to hit arbitrary volume targets.",
      },
      {
        q: "How long until link building affects our rankings?",
        a: "First ranking movements typically appear in 4-6 weeks as Google discovers and weights new links. Significant ranking jumps usually compound over 4-8 months. Domain authority growth (which compounds further) builds over 6-18 months — but the rankings you earn are extremely sticky.",
      },
      {
        q: "Will you build links to our money pages or just blog content?",
        a: "Both — but mostly to content first (blog posts, guides, resource pages), which then pass authority to your money pages via internal linking. Direct money-page links are rarer and only earned through digital PR. This is the safest and most effective link velocity pattern.",
      },
    ],
    relatedServices: ["content-marketing", "cro", "marketing-automation"],
  },
];

/* ─── Helper ──────────────────────────────────────────────── */
const SLUG_ALIASES = {
  "conversion-rate-optimization": "cro",
  "website-design": "web-design",
};

export function getServiceBySlug(slug) {
  const resolved = SLUG_ALIASES[slug] || slug;
  return SERVICE_LIST.find((s) => s.slug === resolved) || null;
}
